import { RegisterComponent } from '@/common/register-component';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { debug, getCalendarRange, parsePreviewLink } from '@/utils/utils';
import { Component, Element, Event, EventEmitter, Host, Listen, Method, Prop, State, Watch, h } from '@stencil/core';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import { Configuration, GroupConfiguration, Notification, NotificationType, ThemeConfig } from '@nylas/core';
import i18next from '@/utils/i18n';
import { CONFIGURATION_EVENT_TYPE, LANGUAGE_CODE } from '@/common/constants';
import { ErrorCategory } from '@/types/index';

/**
 * The `nylas-list-configurations` component displays a list of scheduling pages.
 *
 * @part nlc__create-new-cta - The create new configuration CTA.
 */
@Component({
  tag: 'nylas-list-configurations',
  styleUrl: 'nylas-list-configurations.scss',
  shadow: true,
})
export class NylasListConfigurations {
  /**
   * The host element.
   */
  @Element() host!: HTMLNylasListConfigurationsElement;

  /**
   * The list of configurations to display.
   */
  @Prop() configurations: (Configuration | GroupConfiguration)[] = [];

  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;

  /**
   * The scheduler preview link to use when the user clicks on the preview button.
   * You can use a placeholder `{config.id}` to replace the configuration id anywhere in the link.
   */
  @Prop({ attribute: 'scheduler-preview-link' }) schedulerPreviewLink: string = '';

  /**
   * The next cursor for list configurations, if any.
   */
  @Prop() listConfigurationsNextCursor?: string | null;

  /**
   * @standalone
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;

  /**
   * Event emitted when the component is initialized.
   */
  @Event() initListConfigurations!: EventEmitter<{
    host: HTMLNylasListConfigurationsElement;
    cursor?: string;
  }>;

  /**
   * Event emitted when the user clicks the create event button.
   */
  @Event() createButtonClick!: EventEmitter<HTMLNylasListConfigurationsElement>;

  /**
   * Event emitted when the user clicks the edit event button.
   */
  @Event() editButtonClick!: EventEmitter<{
    host: HTMLNylasListConfigurationsElement;
    configuration: Configuration | GroupConfiguration;
  }>;

  /**
   * Event emitted when the user clicks the delete event button.
   */
  @Event() deleteButtonClick!: EventEmitter<{
    host: HTMLNylasListConfigurationsElement;
    configuration: Configuration | GroupConfiguration;
  }>;

  /**
   * This event is fired when there is an error in the component.
   */
  @Event({ cancelable: false }) readonly nylasSchedulerError!: EventEmitter<{ notification: Notification; host: HTMLElement }>;

  /**
   * Event emitted when the user dismisses the error notification.
   */
  @Event({ cancelable: false }) dismissNotification!: EventEmitter<{ id: string; host: HTMLNylasListConfigurationsElement }>;

  /**
   * The current state of the component.
   */
  @State() private state: 'loading' | 'loaded' | 'error' = 'loading';

  /**
   * The error message to display.
   */
  @State() error?: {
    message: string;
    type: NotificationType | undefined;
    category: ErrorCategory | undefined;
    title: string;
  } = {
    message: '',
    type: NotificationType.Error,
    category: ErrorCategory.Component,
    title: '',
  };

  /**
   * Current cursor for list configurations
   */
  @State() currentCursor: string | null = null;

  /**
   * The list of previous cursors for list configurations
   */
  @State() previousCursors: string[] = [];

  /**
   * State to identiy if the next button should be disabled
   */
  @State() disableNextPageButton: boolean = false;

  /**
   * State to store the configuration id to delete and to show the delete confirmation modal
   */
  @State() deleteConfigurationId: string = '';

  /**
   * State to show Delete button loading state
   */
  @State() showDeleteButtonLoading: boolean = false;

  /**
   * State to show Edit button loading state
   */
  @State() editButtonLoadingId: string = '';

  @Watch('listConfigurationsNextCursor')
  listConfigurationsNextCursorChangedHandler(newVal: string | null, oldVal: string | null) {
    if (!newVal || newVal === oldVal) {
      if (newVal === null) {
        this.disableNextPageButton = true;
      }
      return;
    }
    this.disableNextPageButton = false;
    this.currentCursor = newVal;
  }

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  connectedCallback() {
    debug('[nylas-list-configurations]', 'connectedCallback');
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.host.setAttribute('data-themed', '');
      }
    });
  }

  componentWillLoad() {
    debug('[nylas-list-configurations]', 'componentWillLoad');

    // If we have configurations, we will set the state to loaded.
    if (this.configurations && this.configurations.length > 0) {
      this.state = 'loaded';
    }
  }

  componentDidLoad() {
    debug('[nylas-list-configurations]', 'componentDidLoad');

    // Fire the init event. By default, we will fetch the configurations.
    // This is defined in the eventToProps object in the RegisterComponent decorator.
    this.initListConfigurations.emit({
      host: this.host,
    });
  }

  disconnectedCallback() {
    debug('[nylas-list-configurations]', 'disconnectedCallback');
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.host.style.setProperty(`${key}`, value);
      }
    }
  }

  @Listen('nylasFormDropdownChanged')
  async handleNylasFormDropdownChanged(event: CustomEvent) {
    debug('[nylas-list-configurations]', 'handleNylasFormDropdownChanged', event.detail);
    const { value } = event.detail;
    const [action, index] = value.split('-');
    event.preventDefault();
    switch (action) {
      case 'delete':
        this.deleteConfigurationId = this.configurations[index]?.id;
        break;
      case 'preview':
        window.open(parsePreviewLink(this.schedulerPreviewLink, this.configurations[index]), '_blank');
        break;
      case 'copy':
        await navigator.clipboard.writeText(parsePreviewLink(this.schedulerPreviewLink, this.configurations[index]));
        break;
      default:
        break;
    }
  }

  @Method()
  async registerCallback() {
    // Fire the init event. By default, we will fetch the configurations.
    // This is defined in the eventToProps object in the RegisterComponent decorator.
    this.initListConfigurations.emit({
      host: this.host,
    });
  }

  @Method()
  async getState() {
    return this.state;
  }

  @Method()
  async setState(state: 'loading' | 'loaded' | 'error') {
    this.state = state;
  }

  @Method()
  async setError(error: { message: string; type: NotificationType; category: ErrorCategory; title: string }) {
    this.error = {
      message: error.message,
      type: error.type,
      category: error.category,
      title: error.title || i18next.t('genericErrorTitle'),
    };
    if (this.error?.message) {
      this.showErrorNotification(this.error);
    }
  }

  @Method()
  async hideDeleteConfirmation() {
    this.deleteConfigurationId = '';
    this.showDeleteButtonLoading = false;
  }

  @Method()
  async hideEditButtonLoading() {
    this.editButtonLoadingId = '';
  }

  showErrorNotification(error: { message: string; type: NotificationType | undefined; category: ErrorCategory | undefined; title: string }) {
    this.nylasSchedulerError.emit({
      notification: {
        id: error.message.split(' ').join('-'),
        type: error.type || NotificationType.Error,
        title: error.title,
        category: error.category || ErrorCategory.Component,
        description: error.message,
        ttl: 'none',
      } as Notification,
      host: this.host,
    });
  }

  closeModal(e: Event) {
    e.preventDefault();
    this.deleteConfigurationId = '';
  }

  handleConfirmDeleteButtonClicked(e: Event) {
    e.preventDefault();
    this.showDeleteButtonLoading = true;
    const config = this.configurations.find(config => config.id === this.deleteConfigurationId);
    if (config && config?.id) {
      this.deleteButtonClick.emit({ host: this.host, configuration: config });
    }
  }

  private getDurationString(duration: number) {
    if (!duration) return '—';
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    return `${hours ? hours : ''} ${hours ? `${hours > 1 ? 'hours' : 'hour'}` : ''} ${minutes ? minutes : ''} ${minutes ? `${minutes > 1 ? 'mins' : 'min'}` : ''}`;
  }

  getActionLabelHTML(action: string) {
    const actionStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
    };
    const dangerStyle = {
      color: 'var(--nylas-error)',
    };
    switch (action) {
      case 'preview':
        return (
          <span style={actionStyle}>
            <eye-icon width="16" height="16"></eye-icon>
            {i18next.t('nylasListConfigurations.previewButtonText')}
          </span>
        );
      case 'copy':
        return (
          <span style={actionStyle}>
            <copy-icon width="16" height="16" />
            {i18next.t('nylasListConfigurations.copyLinkButtonText')}
          </span>
        );
      case 'delete':
        return (
          <span style={{ ...actionStyle, ...dangerStyle }}>
            <delete-icon width="15" height="17"></delete-icon>
            {i18next.t('nylasListConfigurations.deleteButtonText')}
          </span>
        );
      default:
        return '';
    }
  }

  previousPageClickedHandler() {
    if (this.previousCursors.length === 0) return;

    this.currentCursor = this.previousCursors.pop() || null;
    const prevCursor = this.previousCursors[this.previousCursors.length - 1] || undefined;
    this.initListConfigurations.emit({
      host: this.host,
      cursor: prevCursor,
    });
  }

  nextPageClickedHandler() {
    if (!this.currentCursor) return;

    this.previousCursors = [...this.previousCursors, this.currentCursor];
    this.initListConfigurations.emit({
      host: this.host,
      cursor: this.currentCursor,
    });
  }

  @RegisterComponent<NylasListConfigurations, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-list-configurations',
    stateToProps: new Map([
      ['schedulerConfig.configurations', 'configurations'],
      ['schedulerConfig.listConfigurationsNextCursor', 'listConfigurationsNextCursor'],
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
      ['schedulerConfig.themeConfig', 'themeConfig'],
    ]),
    localPropsToProp: new Map([['schedulerPreviewLink', 'schedulerPreviewLink']]),
    eventToProps: {
      initListConfigurations: async (event, connector) => {
        const { host, cursor } = event.detail;
        await host.setState('loading');
        const [data, error] = await connector.schedulerConfig.getConfigurations(cursor);
        if (!data && error && error.message) {
          await host.setState('error');
          await host.setError({
            message: error.message,
            type: NotificationType.Error,
            category: ErrorCategory.Component,
            title: i18next.t('nylasEditorTabs.getConfigsError'),
          });
          return;
        }
        await host.setState('loaded');
        // setCursor here
      },
      createButtonClick: async (_, connector) => {
        connector.schedulerConfigStore.resetSelectedConfiguration();
        const enabledEventTypes = Object.entries(connector.schedulerConfigStore.state.enableEventTypes || {}).filter(([_key, value]) => value);
        if (enabledEventTypes.length === 1) {
          connector.schedulerConfigStore.state.action = 'create';
          connector.schedulerConfigStore.state.selectedEventType = CONFIGURATION_EVENT_TYPE[enabledEventTypes[0][0]];
        } else {
          connector.schedulerConfigStore.state.action = 'create-select';
        }
      },
      deleteButtonClick: async (event, connector) => {
        const { host, configuration } = event.detail;
        const [data, error] = await connector.schedulerConfig.deleteConfiguration(configuration.id);
        if (!data && error && error.message) {
          await host.setState('error');
          await host.setError({
            message: error.message,
            type: NotificationType.Error,
            category: ErrorCategory.Component,
            title: i18next.t('nylasEditorTabs.deleteConfigError'),
          });
        }
        await host.hideDeleteConfirmation();
      },
      editButtonClick: async (event, connector) => {
        const { host, configuration } = event.detail;
        if (configuration.type === 'group') {
          const { startTime, endTime } = getCalendarRange(new Date().toISOString(), 'week');
          connector.schedulerConfigStore.state.calendarViewDateRange = {
            start_time: startTime.toString(),
            end_time: endTime.toString(),
          };
          const [data, error] = await connector.schedulerConfig.getGroupEvents(configuration.id, configuration);
          if (!data && error && error.message) {
            // handle error
            await host.setError({
              message: error.message,
              type: NotificationType.Error,
              category: ErrorCategory.Component,
              title: i18next.t('nylasEditorTabsGroup.getGroupEventsError'),
            });
            host.hideEditButtonLoading();
            return;
          }
        }
        connector.schedulerConfigStore.state.action = 'edit';
        connector.schedulerConfigStore.state.selectedConfiguration = configuration;
        host.hideEditButtonLoading();
      },
    },
    fireRegisterEvent: true,
  })
  render() {
    return (
      <Host>
        <nylas-notification></nylas-notification>
        <div class="header">
          <div class="description">
            <h2>{i18next.t('allSchedulingPages')}</h2>
            <p>{i18next.t('createAndManageSchedulingPages')}</p>
          </div>
          {this.configurations && this.configurations.length > 0 && (
            <div class="actions">
              <button-component
                part="nlc__create-new-cta"
                clickHandler={() => {
                  this.createButtonClick.emit(this.host);
                }}
              >
                {i18next.t('createNewSchedulingPageButtonText')}
              </button-component>
            </div>
          )}
        </div>
        <div class="configurations">
          <div class="table-container">
            <table
              class={{
                'no-configurations': this.state !== 'loading' && (!this.configurations || this.configurations.length === 0),
              }}
            >
              <tr
                class={{
                  hide: this.state === 'loading' || (this.configurations && this.configurations.length === 0),
                }}
              >
                <th>{i18next.t('pageTitle')}</th>
                <th>{i18next.t('durationTitle')}</th>
                <th></th>
              </tr>
              {this.state === 'loading' && (
                <tr class="no-configurations">
                  <td colSpan={3}>
                    <div>
                      <h3>{i18next.t('loading')}...</h3>
                      <p>{i18next.t('loadingPagesDescription')}</p>
                    </div>
                  </td>
                </tr>
              )}
              {this.state !== 'loading' && (!this.configurations || this.configurations.length === 0) && (
                <tr class="no-configurations">
                  <td colSpan={3}>
                    <div>
                      <calendar-icon width="48" height="48"></calendar-icon>
                      <h3>{i18next.t('noSchedulingPages')}</h3>
                      <p>{i18next.t('createFirstSchedulingPage')}</p>
                      <button-component
                        part="nlc__create-new-cta"
                        clickHandler={() => {
                          this.createButtonClick.emit(this.host);
                        }}
                      >
                        {i18next.t('createNewSchedulingPageButtonText')}
                      </button-component>
                    </div>
                  </td>
                </tr>
              )}
              {this.state !== 'loading' &&
                this.configurations?.map((config, i) => {
                  const lastTwo = (i === this.configurations.length - 1 || i === this.configurations.length - 2) && this.configurations.length > 3;
                  const groupConfig = config as GroupConfiguration;
                  const regularConfig = config as Configuration;
                  return (
                    <tr key={config?.id}>
                      <td>
                        <div class="title">
                          <div class="config-event-title" title={regularConfig?.event_booking?.title}>
                            {regularConfig?.event_booking?.title || groupConfig?.name}
                          </div>
                          <div class={'config-event-description'}>{regularConfig?.event_booking?.description}</div>
                        </div>
                      </td>
                      <td>
                        <div class="duration">{this.getDurationString(regularConfig?.availability?.duration_minutes)}</div>
                      </td>
                      <td>
                        <div class="actions">
                          <button-component
                            class="edit-button"
                            variant={'basic'}
                            key={`edit-${i}`}
                            disabled={this.editButtonLoadingId === `edit-${i}`}
                            isLoading={this.editButtonLoadingId === `edit-${i}`}
                            clickHandler={event => {
                              event.preventDefault();
                              this.editButtonLoadingId = `edit-${i}`;
                              this.editButtonClick.emit({ host: this.host, configuration: config });
                            }}
                          >
                            {this.editButtonLoadingId === `edit-${i}` ? null : <edit-icon width="16" height="16"></edit-icon>}
                            {i18next.t('editSchedulingPageButtonText')}
                          </button-component>
                          <select-dropdown
                            class={{
                              last: lastTwo,
                            }}
                            name="actions"
                            options={[
                              { value: `preview-${i}`, label: 'Preview', labelHTML: this.getActionLabelHTML('preview') },
                              { value: `copy-${i}`, label: 'Copy link', labelHTML: this.getActionLabelHTML('copy') },
                              { value: `delete-${i}`, label: 'Delete', labelHTML: this.getActionLabelHTML('delete') },
                            ]}
                            defaultSelectedOption={{ value: '', label: '' }}
                            dropdownButtonText="..."
                            withSearch={false}
                            withChevron={false}
                            themeConfig={this.themeConfig}
                            exportparts="sd_dropdown: actions__dropdown, sd_dropdown-button: actions__dropdown-button, sd_dropdown-content: actions__dropdown-content, actions-Copy, actions-Preview, actions-Delete"
                          ></select-dropdown>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </table>
          </div>
          <div class="pagination">
            <button class="arrow left" onClick={() => this.previousPageClickedHandler()} disabled={this.previousCursors.length <= 0}>
              <chevron-icon />
            </button>
            <button class="arrow right" onClick={() => this.nextPageClickedHandler()} disabled={this.disableNextPageButton}>
              <chevron-icon />
            </button>
          </div>
        </div>
        {!!this.deleteConfigurationId && (
          <div class="card-wrapper">
            <div class="card-modal">
              <div class="close-button">
                <button-component tooltip="Close" id="close" variant={'invisible'} onClick={e => this.closeModal(e)} part="nfbf__close-button">
                  <close-icon />
                </button-component>
              </div>
              <div class="header">
                <h2 slot="card-title" part="nfbf__title">
                  {i18next.t('deleteSchedulingPageTitle')}
                </h2>
              </div>
              <div class="description title-desc">
                <p>{i18next.t('deleteSchedulingPageDescription')}</p>
              </div>
              <div
                class={{
                  footer: true,
                }}
              >
                <button-component variant={'basic'} onClick={e => this.closeModal(e)} part="nfbf__button-outline nfbf__cancel-cta">
                  {`${i18next.t('deleteSchedulingPageCancelButton')}`}
                </button-component>
                <button-component
                  variant={'destructive'}
                  onClick={e => this.handleConfirmDeleteButtonClicked(e)}
                  disabled={this.showDeleteButtonLoading}
                  isLoading={this.showDeleteButtonLoading}
                  part="nfbf__button-outline nfbf__reschedule-cta"
                >
                  {`${i18next.t('deleteSchedulingPageConfirmButton')}`}
                </button-component>
              </div>
            </div>
          </div>
        )}
      </Host>
    );
  }
}
